import {
  Button,
  useModal,
  Checkbox,
  Spinner,
} from '@gsa/afp-component-library';
import { useSelector, useDispatch } from 'react-redux';
import { emDashUnicode } from '@gsa/afp-shared-form-utils';
import moment from 'moment';
import {
  RequisitionTransactionType,
  RequisitionType,
} from '../../../../constants/constants';
import {
  STANDARD_ITEM_PARENT_CATEGORY,
  ADDITIONAL_REQUEST_STATUS,
} from '../../utils/additional-utils';
import { getStep } from '../../../leasing/leasing-steps';
import {
  InitialLeasingSteps,
  LeasingSteps,
} from '../../../leasing/leasing-consts';
import { useGetStates } from '../../../../requests/additionals-request';
import { useGetRequisitionSinByTypeahead } from '../../../../requests/additionals-request';
import { useState } from 'react';
import { useCreateLeasingDraft } from '../../../../requests/leasing';
import FundingValidModal from '../funding-valid-modal/funding-valid-modal';
import {
  useGetAdditionalRequestLazy,
  useUpdateAdditonalRequestBackToFunding,
} from '../../../../requests/additionals-request';
import {
  setAdditionalsDraftData,
  setAdditionalsRequestStatus,
  setSinNotAvailable,
  setRequisitionNumber,
  setRequisitionId,
  setAdditionalRequestActivities,
  setRequisitionCreateCheckbox,
} from '../../../../reducers/additionals-reducer';
import { cloneDeep } from 'lodash';

export default function CreateAdditionalRequisition() {
  const {
    additionalsDraftData,
    selectedCustomerAccount,
    selectedStandardItem,
    garageAddress,
    requisitionCreateCheckbox,
  } = useSelector((state) => state.additionalsReducer);
  const dispatch = useDispatch();
  const { data: states } = useGetStates(garageAddress?.country);
  const [getReqSinByTypeahead] = useGetRequisitionSinByTypeahead();
  const [createLeasingDraft, { loading: createLeasingDraftLoading }] =
    useCreateLeasingDraft();
  const fundingValidModal = useModal();
  const [getAdditionalRequest, { loading: isLoading }] =
    useGetAdditionalRequestLazy();
  const [updateAdditonalRequestBackToFunding] =
    useUpdateAdditonalRequestBackToFunding();

  const createRequisition = (e) => {
    const today = moment();
    const requisitionName = [
      selectedCustomerAccount?.legacyCustomerNumber,
      `${today.format('MMMM')}${today.format('DD')}`.toUpperCase(),
      `${today.format('HHmmss')}`,
    ].join('_');

    let selectedVehicleType;
    if (
      selectedStandardItem?.vehicleTypeCode?.parentCategory ===
      STANDARD_ITEM_PARENT_CATEGORY.FED
    ) {
      selectedVehicleType = selectedStandardItem?.vehicleType;
    } else {
      selectedVehicleType = selectedStandardItem?.vehicleTypeCode?.parentCode;
    }
    const payload = {
      createLeasingCustomerAccountInput: {
        customerAccountId: parseInt(selectedCustomerAccount?.customerAccountId),
        customerAccountName: selectedCustomerAccount?.customerName,
        agencyCode: selectedCustomerAccount?.agencyCode,
        bureauCode: selectedCustomerAccount?.bureauCode,
        vehicleReplacements: null,
      },
      createInput: {
        transactionType: RequisitionTransactionType.VEHICLE_LEASE,
        requisitionType: RequisitionType.STANDARD_ORDER,
        agencyCode: '047',
        bureauCode: '09',
        friendlyName: requisitionName,
        zoneId: selectedCustomerAccount?.zoneId,
        // fmcId: selectedCustomerAccount?.id,
        // regionId: vehicleDetails.customer?.regionId,
        standardItemCode: selectedStandardItem.standardItemNumber,
        quantity: 1,
        additionalRequestId: additionalsDraftData?.additionalRequestId,
        clientState: {
          currentStep: getStep(
            InitialLeasingSteps,
            LeasingSteps.COMPARE_AND_SELECT,
          ),
          selectedStandardItem: { ...selectedStandardItem },
          selectedVehicleType: selectedVehicleType,
          destinationType:
            garageAddress.country === 'US' ? 'us-territory' : 'foreign-county',
          selectedState: `${garageAddress.country}-${garageAddress.state}`,
          shipmentLocation: states?.find((s) => s.value === garageAddress.state)
            ?.label,
          shipmentCountry: garageAddress.country,
        },
      },
    };

    getReqSinByTypeahead({
      variables: {
        limit: 1,
        offset: 0,
        searchString: selectedStandardItem.standardItemNumber,
        leasingQuery: true,
      },
    }).then(({ data }) => {
      if (data?.rows?.length > 0) {
        const foundStandardItem = data.rows.find(
          (row) =>
            row.standardItemNumber === selectedStandardItem.standardItemNumber,
        );
        if (foundStandardItem) {
          payload.createInput.clientState.selectedStandardItem.id =
            foundStandardItem?.id;
          payload.createInput.clientState.selectedStandardItem.year =
            foundStandardItem?.year;
          payload.createInput.clientState.selectedStandardItem.status =
            foundStandardItem?.status;

          createLeasingDraft({
            variables: payload,
          }).then(async ({ data }) => {
            window.open(`/lease/${data.requisitionId}`, '_blank');

            const { data: additionalRequest } = await getAdditionalRequest({
              variables: {
                additionalRequestId: additionalsDraftData?.additionalRequestId,
                agencyCode: selectedCustomerAccount?.agencyCode,
                bureauCode: selectedCustomerAccount?.bureauCode,
                officeCode: selectedCustomerAccount?.officeCode,
              },
            });
            dispatch(setAdditionalsDraftData(additionalRequest));
            dispatch(
              setAdditionalsRequestStatus(additionalRequest?.requestStatusCode),
            );
            const activities = cloneDeep(
              additionalRequest.additionalRequestActivities,
            );
            const sortedActivities = activities.sort((a, b) => {
              return new Date(b.createdAt) - new Date(a.createdAt);
            });
            if (activities?.length) {
              dispatch(setAdditionalRequestActivities(sortedActivities));
            }
            dispatch(setRequisitionId(data.requisitionId));
            dispatch(setRequisitionNumber(data.requisitionNumber));
            dispatch(setSinNotAvailable(false));
          });
        } else {
          dispatch(setSinNotAvailable(true));
        }
      } else {
        dispatch(setSinNotAvailable(true));
      }
    });
  };

  const handleReturnFundingValidation = async () => {
    updateAdditonalRequestBackToFunding({
      variables: {
        requestStatusCode: ADDITIONAL_REQUEST_STATUS.PENDING_GSA_APPROVAL,
        additionalRequestId: additionalsDraftData?.additionalRequestId,
        returnFunding: true,
      },
    }).then(({ data }) => {
      getAdditionalRequest({
        variables: {
          additionalRequestId: additionalsDraftData?.additionalRequestId,
        },
      }).then(({ data }) => {
        dispatch(setAdditionalsDraftData(data));
        dispatch(setAdditionalsRequestStatus(data?.requestStatusCode));
        window.scrollTo(0, 0);
      });
    });
  };

  if (createLeasingDraftLoading) {
    return <Spinner size="large" />;
  }

  return (
    <>
      <div>
        <div data-testid="Create-requisition-section">
          <strong>Create a requisition</strong>
          <div className="display-flex flex-align-center justify-content-start padding-top-2">
            <Checkbox
              name={'verify'}
              onChange={() => {
                dispatch(
                  setRequisitionCreateCheckbox(!requisitionCreateCheckbox),
                );
              }}
              checked={requisitionCreateCheckbox}
            />
            <div className="padding-left-2">
              I have verified that there are no existing vehicles in inventory
              that can fill this need.
            </div>
          </div>

          <Button
            className="margin-top-4"
            type="submit"
            leftIcon={{ name: 'add' }}
            variant="primary"
            onClick={() => {
              if (additionalsDraftData.validFunding) {
                createRequisition();
              } else {
                fundingValidModal.openModal();
              }
            }}
            label="Create a requisition"
            disabled={!requisitionCreateCheckbox}
          />
        </div>
      </div>
      <FundingValidModal
        isOpen={fundingValidModal.isOpen}
        handleClose={fundingValidModal.closeModal}
        handleSubmit={handleReturnFundingValidation}
      />
    </>
  );
}
